/**
 * Virtual Line Clamp Calculator
 * Uses virtual DOM calculations for optimal performance
 * Based on representative blog post with longest title and performance badge
 */

class DynamicLineClamp {
  constructor() {
           // Virtual height constants (based on CSS values and typical content)
           this.virtualHeights = {
             // Image heights (fixed for consistent calculations)
             imageHeight: {
               mobile: 450,    // Approximate mobile image height (100% of container)
               tablet: 600,    // Approximate tablet image height (100% of container)
               desktop: 700    // Approximate desktop image height (100% of container)
             },
      // Element heights (based on typical content)
      categoryHeight: 20,      // Category badge height (performance badge)
      titleHeight: 60,         // Longest title height (2-3 lines)
      publishInfoHeight: 20,   // Publish info height
      buttonHeight: 40,        // Button height
      // Spacing values (matching CSS variables)
      spacing125: 10,          // --spacing-125 (category/title/publish margin-bottom)
      spacing200: 16,          // --spacing-200 (excerpt margin-top)
      spacing400: 32,          // --spacing-400 (image margin-bottom)
      // Line height
      lineHeight: 24           // Typical line height for excerpt text
    };

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.calculateLineClamps());
    } else {
      this.calculateLineClamps();
    }

    // Recalculate on window resize
    window.addEventListener('resize', this.debounce(() => this.calculateLineClamps(), 250));
  }

  calculateLineClamps() {
    const elements = document.querySelectorAll('.blog-posts-meta__excerpt--grid');
    
    elements.forEach(element => {
      this.calculateElementLineClamp(element);
    });
  }

  getViewportSize() {
    const width = window.innerWidth;
    if (width < 720) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  isSingleColumnLayout() {
    // Check if we're in single column layout (mobile or when CSS grid is set to 1 column)
    const width = window.innerWidth;
    return width < 720; // Single column on mobile
  }

  calculateElementLineClamp(element) {
    const container = element.closest('.blog-posts-meta');
    if (!container) return;

    const viewport = this.getViewportSize();
    const heights = this.virtualHeights;
    
    // Calculate available height for text content
    // Start with the total blog post height (image + content)
    let availableHeight = heights.imageHeight[viewport]// + heights.spacing200; // Image + margin-bottom
    
    // Subtract all the fixed content heights and spacing
    const category = container.querySelector('.blog-posts-meta__category');
    const publishInfo = container.querySelector('.blog-posts-meta__publish-info');
    
    // Subtract category (performance badge) if present
    if (category) {
      availableHeight -= heights.categoryHeight + heights.spacing125; // Category + margin-bottom
    }
    
    // Subtract title and its margin
    availableHeight -= heights.titleHeight + heights.spacing125; // Title + margin-bottom
    
    // Subtract publish info if present
    if (publishInfo) {
      availableHeight -= heights.publishInfoHeight + heights.spacing125; // Publish info + margin-bottom
    }
    
    // Subtract excerpt margin-top
    availableHeight -= heights.spacing200; // Excerpt margin-top
    
    // Subtract button height (margin-top: auto will create spacing automatically)
    availableHeight -= heights.buttonHeight; // Button height
    
    // Add conservative buffer to protect image space
    availableHeight -= 20; // Additional buffer to prevent image squishing
    
    // Calculate optimal number of lines
    const optimalLines = Math.floor(availableHeight / heights.lineHeight);
    
           // More generous limits now that images are properly sized
           // Allow more lines in single column layout
           const isSingleColumn = this.isSingleColumnLayout();
           let maxLines;
           
           if (isSingleColumn) {
             // More generous limits for single column (more vertical space available)
             maxLines = category ? 8 : 9; // More lines when badge is present
           } else {
             // Increased limits for multi-column layouts now that images are properly sized
             maxLines = category ? 6 : 7; // More lines when badge is present
           }
    
    const clampedLines = Math.max(2, Math.min(optimalLines, maxLines));
    
    // Apply the calculated line-clamp
    element.style.webkitLineClamp = clampedLines;
    element.style.lineClamp = clampedLines;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize the dynamic line clamp calculator
new DynamicLineClamp();